import React, { useMemo } from "react";
import { CheckCircleFilled } from "@ant-design/icons";
import { FaSuitcase, FaSuitcaseRolling } from "react-icons/fa";
import {
  buildFlightCardLegRowsFromDeal,
  formatExtrasSegmentDirectionLabel,
  mapPaxListWithPassengerNames,
  mapRawLdsSupplementsToMockupExtras,
} from "../../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import { resolveFlightBaggageDisplayState } from "../../Components/FlightBookingFlowMockup/adapters/flightBaggageStepPolicy";
import { formatFx, formatIls } from "../packageProfileMockup/mockPackageProfileData";

function optionTitle(row) {
  const t = String(row?.type || "").toUpperCase();
  const kind = t === "TROLLEY" ? "טרולי" : "מזוודה";
  const weight = String(row?.weightLabel || "").trim();
  return weight ? `${kind} - ${weight}` : row?.titleLine || kind;
}

function directionMeta(row, numSegments) {
  const dir = formatExtrasSegmentDirectionLabel(row.segmentIndex ?? 0, numSegments);
  if (dir === "הלוך") return "טיסת הלוך";
  if (dir === "חזור") return "טיסת חזור";
  return dir || row.perPaxDirectionHe || "";
}

function segmentHeading(legRow, segmentIndex, numSegments) {
  const dir =
    formatExtrasSegmentDirectionLabel(segmentIndex, numSegments) ||
    (segmentIndex === 0 ? "הלוך" : "חזור");
  if (!legRow) return `טיסת ${dir}`;
  const route = [legRow.originCode, legRow.destinationCode]
    .filter(Boolean)
    .join(" → ");
  return route ? `טיסת ${dir} · ${route}` : `טיסת ${dir}`;
}

/**
 * Package-profile baggage purchase UI (.hpm-baggage-purchase) for unified flight+hotel.
 * Selections write into extras.supplementPaxSelections (LDS mockup keys).
 */
export default function UnifiedHpmBaggagePurchase({
  rawDeal,
  roomInfo,
  passengers,
  extras,
  onTogglePax,
  disabled = false,
}) {
  const baggageState = useMemo(
    () => resolveFlightBaggageDisplayState(rawDeal),
    [rawDeal]
  );

  const legRows = useMemo(
    () => (rawDeal ? buildFlightCardLegRowsFromDeal(rawDeal) : []),
    [rawDeal]
  );

  const mapped = useMemo(
    () => (rawDeal ? mapRawLdsSupplementsToMockupExtras(rawDeal) : []),
    [rawDeal]
  );
  const selectable = useMemo(
    () => mapped.filter((row) => Number(row?.fareAmount) > 0),
    [mapped]
  );

  /** Prefer real flight legs (like ExtrasStepCard), not max(segmentIndex)+1. */
  const numSegments = Math.max(
    1,
    legRows.length ||
      selectable.reduce((m, row) => Math.max(m, (row.segmentIndex ?? 0) + 1), 0)
  );

  const rowsBySegment = useMemo(() => {
    const m = new Map();
    for (const row of selectable) {
      const idx = Math.max(0, Number(row.segmentIndex) || 0);
      if (!m.has(idx)) m.set(idx, []);
      m.get(idx).push(row);
    }
    return m;
  }, [selectable]);

  const segmentIndices = useMemo(() => {
    if (numSegments >= 2) {
      return Array.from({ length: numSegments }, (_, i) => i);
    }
    return [0];
  }, [numSegments]);

  const paxList = useMemo(
    () => mapPaxListWithPassengerNames({ roomInfo, passengers }),
    [roomInfo, passengers]
  );

  const includedLines =
    baggageState.displayState === "included"
      ? baggageState.includedLines || []
      : [];

  if (!rawDeal) {
    return (
      <p className="hpm-baggage-purchase__hint" role="status">
        אין נתוני טיסה לבחירת כבודה — בחרו טיסה בשלב הקודם.
      </p>
    );
  }

  if (!selectable.length) {
    return (
      <div className="hpm-baggage-purchase">
        {includedLines.length > 0 ? (
          <div className="hpm-baggage-purchase__included" role="status">
            <FaSuitcase aria-hidden />
            <div>
              <strong>כלול בטיסה</strong>
              <p>{includedLines.join(" · ")}</p>
            </div>
          </div>
        ) : null}
        <p className="hpm-baggage-purchase__hint" role="status">
          {includedLines.length > 0
            ? "אין תוספות כבודה לרכישה מעבר לכלול בטיסה"
            : "אין תוספות כבודה זמינות לטיסה זו כרגע — ניתן להמשיך ללא תוספת"}
        </p>
      </div>
    );
  }

  return (
    <div className="hpm-baggage-purchase">
      {includedLines.length > 0 ? (
        <div className="hpm-baggage-purchase__included" role="status">
          <FaSuitcase aria-hidden />
          <div>
            <strong>כלול בטיסה</strong>
            <p>{includedLines.join(" · ")}</p>
          </div>
        </div>
      ) : null}

      <div className="hpm-baggage-purchase__list">
        {paxList.map((pax) => {
          const selectedCount = selectable.filter((row) =>
            (extras?.supplementPaxSelections?.[row.supplementKey] || []).includes(
              pax.id
            )
          ).length;
          const legendId = `usm-bag-legend-${pax.id}`;
          return (
            <fieldset key={pax.id} className="hpm-baggage-purchase__pax">
              <legend id={legendId}>{pax.labelHe || pax.label || "נוסע"}</legend>
              {segmentIndices.map((segIdx) => {
                const rows = (rowsBySegment.get(segIdx) || []).filter(
                  (row) => row.allowsInfant || pax.role !== "infant"
                );
                if (!rows.length && numSegments < 2) return null;
                return (
                  <div
                    key={`seg-${segIdx}`}
                    className="hpm-baggage-purchase__segment"
                  >
                    {numSegments >= 2 ? (
                      <p className="hpm-baggage-purchase__segment-title">
                        {segmentHeading(legRows[segIdx], segIdx, numSegments)}
                      </p>
                    ) : null}
                    {rows.length ? (
                      <div
                        className="hpm-baggage-purchase__options"
                        role="group"
                        aria-labelledby={legendId}
                      >
                        {rows.map((row) => {
                          const selectedIds =
                            extras?.supplementPaxSelections?.[
                              row.supplementKey
                            ] || [];
                          const selected = selectedIds.includes(pax.id);
                          const ils = Number(
                            extras?.supplementUnitFaresIls?.[row.supplementKey]
                          );
                          const usd = Number(row.fareAmount);
                          const isTrolley =
                            String(row.type || "").toUpperCase() === "TROLLEY";
                          const meta = [
                            row.weightLabel,
                            directionMeta(row, numSegments),
                          ]
                            .filter(Boolean)
                            .join(" · ");
                          return (
                            <button
                              key={`${row.supplementKey}-${pax.id}`}
                              type="button"
                              role="checkbox"
                              aria-checked={selected}
                              disabled={disabled}
                              className={`hpm-baggage-purchase__opt${
                                selected ? " is-selected" : ""
                              }`}
                              onClick={() =>
                                onTogglePax?.(
                                  row.supplementKey,
                                  pax.id,
                                  !selected
                                )
                              }
                            >
                              <span
                                className="hpm-baggage-purchase__opt-icon"
                                aria-hidden
                              >
                                {isTrolley ? (
                                  <FaSuitcaseRolling />
                                ) : (
                                  <FaSuitcase />
                                )}
                              </span>
                              <span className="hpm-baggage-purchase__opt-body">
                                <span className="hpm-baggage-purchase__opt-title">
                                  {optionTitle(row)}
                                </span>
                                {meta ? (
                                  <span className="hpm-baggage-purchase__opt-meta">
                                    {meta}
                                  </span>
                                ) : null}
                              </span>
                              <span className="hpm-baggage-purchase__opt-price">
                                <span className="hpm-baggage-purchase__opt-ils">
                                  {Number.isFinite(ils) && ils >= 0
                                    ? formatIls(Math.ceil(ils))
                                    : formatFx(usd, row.currency || "USD")}
                                </span>
                                {Number.isFinite(ils) &&
                                ils >= 0 &&
                                Number.isFinite(usd) ? (
                                  <span className="hpm-baggage-purchase__opt-fx">
                                    ({formatFx(usd, row.currency || "USD")})
                                  </span>
                                ) : null}
                              </span>
                              {selected ? (
                                <span
                                  className="hpm-baggage-purchase__opt-check"
                                  aria-hidden
                                >
                                  <CheckCircleFilled />
                                </span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="hpm-baggage-purchase__hint" role="status">
                        אין תוספת כבודה לכיוון זה
                      </p>
                    )}
                  </div>
                );
              })}
              <p className="hpm-baggage-purchase__hint">
                {selectedCount > 0
                  ? "לחצו שוב על האפשרות כדי להסיר"
                  : "ללא תוספת כבודה לנוסע זה"}
              </p>
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}
